import { Divider, Stack } from '@mui/material';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { DataGridFloatingActionToolbarBase, DeleteDialog, TextButton } from '@retail/components';
import { EditPriceSuggestionsDialog, FixedPricesTable } from '@retail/price-suggestion/components';
import { useLocalPriceSuggestionActions } from '@retail/price-suggestion/context';
import { useFetchPriceSuggestionPrices } from '@retail/price-suggestion/data-access';
import { usePriceSuggestionTFunction } from '@retail/price-suggestion/i18n';
import { PriceSuggestionPrice } from '@retail/price-suggestion/types';
import { useDisclosure } from '@shared/hooks';
import { MutableRefObject, useCallback, useMemo, useState } from 'react';

interface Props {
  assortmentId: number;
  apiRef: MutableRefObject<GridApiPremium | null>;
  selectedSupplier?: number;
  selectedDiscountGroups?: string[];
  selectedProductGroups?: string[];
  query?: string;
}

export function FixedPriceTableContainer({
  assortmentId,
  apiRef,
  selectedSupplier,
  selectedDiscountGroups,
  selectedProductGroups,
  query,
}: Props) {
  const t = usePriceSuggestionTFunction();

  const { isBrandContext } = useSelectedOrgUnit();
  const { data: priceSuggestionPricesDTO = [], isLoading: isLoadingFetchPrices } =
    useFetchPriceSuggestionPrices(
      {
        assortmentId,
        includeOwner: true,
        suggestionTypes: [isBrandContext ? 'BRAND' : 'LOCAL'],
        mgSupplierIds: selectedSupplier ? [selectedSupplier] : undefined,
        discountGroupIds: selectedDiscountGroups,
        groupNumbers: selectedProductGroups,
        query,
      },
      { suspense: false }
    );

  const priceSuggestionPrices = useMemo(
    () => priceSuggestionPricesDTO.map(PriceSuggestionPrice.fromDto) ?? [],
    [priceSuggestionPricesDTO]
  );

  const [checkedItems, checkItems] = useState<PriceSuggestionPrice[]>([]);
  const [itemToDelete, setItemToDelete] = useState<PriceSuggestionPrice | undefined>(undefined);
  const [itemToEdit, setItemToEdit] = useState<PriceSuggestionPrice | undefined>(undefined);
  const { isOpen: isBulkEditOpen, onToggle: toggleBulkEdit } = useDisclosure();
  const {
    isOpen: isBulkDeleteOpen,
    onOpen: openBulkDelete,
    onClose: closeBulkDelete,
  } = useDisclosure();

  const { deleteLocalPriceSuggestions, editLocalPriceSuggestions } =
    useLocalPriceSuggestionActions();

  const handleDeleteFixedPrice = useCallback(() => {
    deleteLocalPriceSuggestions(itemToDelete ? [itemToDelete] : checkedItems);
  }, [checkedItems, deleteLocalPriceSuggestions, itemToDelete]);

  return (
    <Stack flexGrow={1} position="relative">
      <FixedPricesTable
        priceSuggestionPrices={priceSuggestionPrices}
        isLoadingFetchPrices={isLoadingFetchPrices}
        apiRef={apiRef}
        checkedItems={checkedItems}
        checkItems={checkItems}
        setItemToDelete={setItemToDelete}
        setItemToEdit={setItemToEdit}
      />
      {!!checkedItems.length && (
        <DataGridFloatingActionToolbarBase
          totalItems={priceSuggestionPricesDTO.length ?? 0}
          itemsChecked={checkedItems.length}
          resetChecked={() => checkItems([])}
          actions={
            <>
              <TextButton sx={{ ':hover': { color: 'common.black' } }} onClick={toggleBulkEdit}>
                {t('priceSuggestion.actions.editSelection')}
              </TextButton>

              <Divider orientation="vertical" sx={{ height: 'auto' }} />

              <TextButton sx={{ ':hover': { color: 'common.black' } }} onClick={openBulkDelete}>
                {t('priceSuggestion.actions.deleteSelection')}
              </TextButton>
            </>
          }
        />
      )}
      {(isBulkDeleteOpen || itemToDelete) && (
        <DeleteDialog
          isOpen
          dialogTitle={t('priceSuggestion.deletePrices', {
            count: itemToDelete ? 1 : checkedItems.length,
          })}
          message={t('priceSuggestion.deletePricesDesc')}
          onDelete={handleDeleteFixedPrice}
          onClose={() => {
            closeBulkDelete();
            setItemToDelete(undefined);
          }}
        />
      )}
      {(isBulkEditOpen || itemToEdit) && (
        <EditPriceSuggestionsDialog
          itemsToEdit={itemToEdit ? [itemToEdit] : checkedItems}
          editLocalPriceSuggestions={editLocalPriceSuggestions}
          onClose={() => {
            closeBulkDelete();
            checkItems([]);
            setItemToEdit(undefined);
          }}
        />
      )}
    </Stack>
  );
}
